import { getRequestDateRange } from "../../../../utils/date/requestDateRange";
import { useReportEffect } from "../../../../hooks/useReportEffect";
import { ReportTop } from "../../../../components/Dashboard/ReportLayout";
import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as BagFood } from "../../../../assets/svg/icons/bagFood.svg";
import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import { flattenSelection, selectionToNumericIds, selectionToSortedIdsKey, useFilterContextV2 } from "../../../../contexts/FilterContextV2";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IOptionProps } from "../../../../@types/reactSelect/IOption";
import { DropdownV2, MultiDropdownV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { daysBetweenDates } from "../../../../utils/date/daysBetweenDays";
import { exportSupplementationConsumptionToExcel } from "../../../../utils/excel/exportSupplementationConsumption";
import { getLocalizedApiName } from "../../../../utils/i18n/getLocalizedApiName";

export default function DashboardSupplementationProductConsumption() {
  const { t, i18n } = useTranslation();
  const {
    rangeDays,
    onBoardingDate,
    setRangeDays,
    setHideMicroAreaFilter,
    setHideBatchFilter,
    setDashboard,
    setSideBarOptionSelected,
    setSelectRangeDate
  } = useFilter();

  const { selectedFarms, selectedAreas } = useFilterContextV2();

  const farmsKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );
  const areasKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
  } = useForm();

  const [optionsClassification, setOptionsClassification] = useState<IOptionProps[]>([]);
  const [optionsProduct, setOptionsProduct] = useState<IOptionProps[]>([]);
  const [consumptionByMonth, setConsumptionByMonth] = useState<IConsumptionByMonth[]>();
  const selectedClassification = watch("classification")
  const selectedProducts = watch("products")
  const [mapMounths] = useState(new Map());

  useEffect(() => {
    const now = new Date();
    const lastYear = new Date(now.getFullYear() - 1, now.getMonth(), now.getDay());

    setRangeDays({
      from: daysBetweenDates(now, onBoardingDate || lastYear) > 365 ? lastYear : onBoardingDate,
      to: now
    })
  }, [onBoardingDate]);

  useEffect(() => {
    setHideMicroAreaFilter(true)
    setHideBatchFilter(true)
    setSideBarOptionSelected("DASHBOARD")
    setDashboard("SUPPLEMENTATION")
    setSelectRangeDate(2)
    document.title = Constants.TITLE_DASHBOARD;
  }, []);

  useReportEffect((axios) => {
    const farmIds = selectionToNumericIds(selectedFarms);
    const areaIds = selectionToNumericIds(selectedAreas);
    if (
      !farmIds.length || !areaIds.length || !rangeDays ||
      !selectedClassification ||
      !selectedProducts
    ) {
      return;
    }

    const params = {
      areaIds,
      classificationId: selectedClassification.value,
      supplementId: selectedProducts.map((item: any) => item.value),
    }

    const farmQueries = farmIds.map(farmId => ({
      farmId,
      areaIds: flattenSelection(selectedAreas)
        .filter(area => Number(area.info?.farm?.id) === farmId)
        .map(area => Number(area.value)),
    })).filter(query => query.areaIds.length > 0);

    Promise.all(farmQueries.map(query => axios.get<IConsumptionByMonth[]>("/supplement/consumption-by-month", {
      params: { ...params, ...query, ...getRequestDateRange(rangeDays.from, rangeDays.to) },
      paramsSerializer: params => {
        return new URLSearchParams(params).toString();
      }
    }))).then((responses) => {
      const resposeConsumption = responses.flatMap(response => response.data);
      mapMounths.clear();
      resposeConsumption.map((area) => area.months.map((month) => {
        const existingMonths = mapMounths.get(month.year_month) || [];
        mapMounths.set(month.year_month, [...existingMonths, month.total_consumption]);
      }));

      setConsumptionByMonth(resposeConsumption);
    }).catch(() => undefined)
  }, [rangeDays, farmsKey, areasKey, selectedClassification, selectedProducts]);

  useReportEffect((axios) => {
    Promise.all([
      axios.get("/products/supplement/classification"),
      axios.get("/products/all"),
    ]).then(([{ data: classifications }, { data: products }]) => {
      const classificationOptions = classifications.map((classification: any) => ({
        label: getLocalizedApiName(classification.name, i18n.language),
        value: classification.id,
      }));
      setOptionsClassification(classificationOptions);

      const selected = getValues("classification");
      if (selected) {
        setValue(
          "classification",
          classificationOptions.find((option: IOptionProps) => option.value === selected.value) || null
        );
      }

      setOptionsProduct(products.map((product: any) => {
        const classificationId = product.classification_id || product.classification?.id || classifications.find(
          (classification: any) => [classification.name.pt, classification.name.es].includes(product.classification)
        )?.id;

        return {
          label: product.trade_name,
          value: product.id,
          info: classificationId,
        };
      }));
    }).catch(() => undefined);
  }, [i18n.language]);

  return (
    <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
      <DateModal />

      <form onSubmit={() => handleSubmit(() => { })}>
        <div className="cattleMap_page">
          <ReportTop icon={<BagFood height={72} />} title={t("navigation.supplementation")} subtitle={t("navigation.monthlyConsumption")}>
            <div>
              <FilterDateDashboard
                onExportExcel={() => {
                  if (consumptionByMonth && consumptionByMonth.length > 0) {
                    try {
                      exportSupplementationConsumptionToExcel(
                        consumptionByMonth,
                        mapMounths,
                        rangeDays?.from,
                        rangeDays?.to
                      );
                    } catch (error) {
                      console.error('Erro ao exportar:', error);
                      alert(t("reports.exportError"));
                    }
                  }
                }}
              />
              <br />

              <div style={{ width: "100%", maxWidth: 400 }}>
                <Controller
                  name="classification"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DropdownV2
                      name="classification"
                      label={t("reports.classification").toUpperCase()}
                      placeholder={t("reports.select")}
                      menuMount="static"
                      emptyMessage={t("reports.noOptions")}
                      options={optionsClassification.filter((classification) => optionsProduct.map(el => el.info).includes(classification.value))}
                      value={field.value ?? null}
                      onChange={(opt) => {
                        field.onChange(opt);
                        setValue("products", null);
                      }}
                      onBlur={field.onBlur}
                      error={error?.message}
                    />
                  )}
                />

                <Controller
                  name="products"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <MultiDropdownV2
                      name="products"
                      label={t("reports.product").toUpperCase()}
                      placeholder={t("reports.select")}
                      menuMount="static"
                      emptyMessage={t("reports.noProducts")}
                      options={selectedClassification ? optionsProduct.filter(product => product.info == selectedClassification.label) : []}
                      value={field.value ?? []}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={error?.message}
                    />
                  )}
                />
              </div>
            </div>
          </ReportTop>
        </div>

        <br />

        {consumptionByMonth ?
          <div className="tableDashboard" id={"analise_geral"}>
            <h2>{t("reports.consumptionByMonth")}</h2>
            <table>
              <thead style={{ color: "#fff" }}>
                <th scope="col" rowSpan={2} style={{ background: "#324440", width: "280px", textAlign: "left" }}>
                  {t("reports.area")}
                </th>
                {Array.from(mapMounths.keys()).map((month, index) => (
                  <th
                    key={index}
                    style={{
                      background: index % 2 === 0 ? "#808080" : "#324440",
                      width: index === 13 ? "210px" : "150px"
                    }}
                  >
                    {month}
                  </th>
                ))}
                <th style={{
                  background: "#808080",
                  width: "150px"
                }}>
                  {t("reports.annualAverage")}
                </th>
                <th scope="col" style={{
                  background: "#324440",
                  width: "210px"
                }}>
                  {t("reports.replacementFrequency")}
                </th>

              </thead>
              {consumptionByMonth?.map((area, index) =>
                <tbody>
                  {
                    <tr className={index % 2 === 0 ? " bg_white" : "bg_gray"}>
                      <th scope="row">{area.area_initials}</th>
                      {area.months.map((month) => <td>{month.total_consumption}</td>)}
                      <td>{area.months.length > 0 ? (Number(area.months.reduce((acc, item) => acc + item.total_consumption, 0) / 12).toFixed(2)) : 0}</td>
                      <td>{area.replacement_frequency}</td>
                    </tr>
                  }
                </tbody>
              )}
              <tfoot
                style={{
                  background: "#324440",
                  color: "#fff"
                }}
              >
                <th style={{
                  textAlign: "left"
                }}>{t("reports.farmSum")}</th>
                {Array.from(mapMounths.values()).map((month: number[], index) =>
                  <th key={index}>
                    {month.reduce((acc, item) => acc + item, 0)}
                  </th>
                )}
                <th>
                  {Number(Array.from(mapMounths.values()).flat().reduce((acc, item) => acc + item, 0) / 12).toFixed(2)}
                </th>
                <th />
              </tfoot>
            </table>
          </div>
          : <></>
        }

      </form >
    </div >
  );
}
